import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import { addPeriod, toDateString } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { Jobs } from "models/Job";
import { AppAsyncThunkConfig } from "store";
import { State } from "./planner.store";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

export const asyncQueries = {
  getPlannerJob: createAppAsyncThunk(
    "planner/getPlannerJob",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { root } = getState();
      const selectedJobId = root.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getJobPlanner, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.jobPlanner) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getPlannerJobs: createAppAsyncThunk(
    "planner/getPlannerJobs",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      const filter = getState().planner.plannerFilter;
      const { data, errors } = await graphqlRequest(sdk.getJobsPlanner, {
        variables: {
          plannerFilter: {
            jobId: filter.jobId,
            toDate: toDateString(addPeriod(filter.typeDate)),
            responseDate: toDateString(filter.specificDate),
            engineerIds: filter.selectedEngineers.map((x) => x?.id),
            symptomIds: filter.selectedSymptoms.map((x) => x?.code) as string[],
            areaCode: filter.areaCode,
            customerId: filter.customer?.id,
            city: filter.city,
            postalCode: filter.postalCode,
            plannerNode: filter.plannerNode?.nodeKey ?? "",
            department: technicianSettings.plannerAllDept ? filter.department?.code : "",
          },
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.jobsPlanner) return rejectWithValue("something went wrong");
      return data;
    },
  ),
  getFiles: createAppAsyncThunk(
    "planner/getFiles",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { root } = getState();
      const selectedJobId = root.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getFiles, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.files) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getLinks: createAppAsyncThunk(
    "planner/getLinks",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { root } = getState();
      const selectedJobId = root.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getDocumentWebLinks, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.links) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getNotes: createAppAsyncThunk(
    "planner/getNotes",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { root } = getState();
      const selectedJobId = root.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getWorkNotes, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.workNotes) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getVisits: createAppAsyncThunk(
    "planner/getVisits",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { root } = getState();
      const selectedJobId = root.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getJobVisits, {
        variables: {
          max: 20,
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.jobVisits) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getComments: createAppAsyncThunk(
    "planner/getComments",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { root } = getState();
      const selectedJobId = root.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getComments, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.comments) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getCustomers: createAppAsyncThunk(
    "planner/getCustomers",
    async (variables: { nameFilter: string }, { rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getCustomersSimple, {
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.customers) return rejectWithValue("something went wrong");
      return data;
    },
  ),
};

export const queryBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(asyncQueries.getPlannerJob.pending, (state) => {
    state.loadingJob = true;
    return state;
  });
  builder.addCase(asyncQueries.getPlannerJob.rejected, (state) => {
    state.loadingJob = false;
    return state;
  });
  builder.addCase(asyncQueries.getPlannerJob.fulfilled, (state, { payload: { data } }) => {
    let serviceJobs = data.jobPlanner;
    let newJobs = serviceJobs.reduce((obj, next) => {
      obj[next.id] = {
        ...next,
        files: [],
        workNotes: [],
        visits: [],
        comments: [],
        links: [],
      };

      return obj;
    }, {} as Jobs);

    state.jobs = newJobs;
    state.loadingJob = false;
    return state;
  });
  builder.addCase(asyncQueries.getPlannerJobs.pending, (state) => {
    state.loadingJobs = true;
    return state;
  });
  builder.addCase(asyncQueries.getPlannerJobs.rejected, (state) => {
    state.loadingJobs = false;
    return state;
  });
  builder.addCase(asyncQueries.getPlannerJobs.fulfilled, (state, { payload: data }) => {
    let serviceJobs = data.jobsPlanner;
    let newJobs = serviceJobs.reduce((obj, next) => {
      obj[next.id] = {
        ...next,
        files: [],
        workNotes: [],
        visits: [],
        comments: [],
        links: [],
      };

      return obj;
    }, {} as Jobs);

    state.jobs = newJobs;
    state.loadingJobs = false;
    return state;
  });
  builder.addCase(
    asyncQueries.getFiles.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.jobs[selectedJobId].files = [...data.files];
      return state;
    },
  );
  builder.addCase(
    asyncQueries.getLinks.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.jobs[selectedJobId].links = [...data.links];
      return state;
    },
  );
  builder.addCase(
    asyncQueries.getNotes.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.jobs[selectedJobId].workNotes = [...data.workNotes];
      return state;
    },
  );
  builder.addCase(
    asyncQueries.getVisits.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.jobs[selectedJobId].visits = [...data.jobVisits];
      return state;
    },
  );
  builder.addCase(asyncQueries.getComments.pending, (state) => {
    state.commentsLoading = true;
    return state;
  });
  builder.addCase(asyncQueries.getComments.rejected, (state) => {
    state.commentsLoading = false;
    return state;
  });
  builder.addCase(
    asyncQueries.getComments.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.jobs[selectedJobId].comments = [...data.comments];
      state.commentsLoading = false;
      return state;
    },
  );
  builder.addCase(asyncQueries.getCustomers.pending, (state) => {
    state.loadingCustomers = true;
    return state;
  });
  builder.addCase(asyncQueries.getCustomers.rejected, (state) => {
    state.loadingCustomers = false;
    return state;
  });
  builder.addCase(asyncQueries.getCustomers.fulfilled, (state, { payload: { customers } }) => {
    state.loadingCustomers = false;
    state.customers = [...customers];
    return state;
  });
};
